import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Skeleton,
  Tab,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import {
  EditOutlined,
  QrCode2Outlined,
  WarningAmberOutlined,
  RestoreOutlined,
  BuildCircleOutlined,
  ChevronRight,
  PowerSettingsNewOutlined,
  InfoOutlined,
  NoteAddOutlined,
  HandymanOutlined,
  ReportProblemOutlined,
} from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { getMachine, restoreMachine, setError, setSuccess } from "../customerMachinerySlice";
import { CustomerMachineState } from "../models/models";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import ActionToast from "../../../shared/components/ActionToast";
import { NotificationMessage } from "../../../shared/models/models";
import PartsAndConsumables from "./PartsAndConsumables";
import ServiceReports from "./ServiceReports";
import ManualsAndSoftware from "./manualsAndSoftware/ManualsAndSoftware";
import MachineSpecs from "./MachineSpecs";
import MachineEditDialog from "./MachineEditDialog";
import MachineQRCodeDialog from "./MachineQRCodeDialog";
import DecommissionDialog from "./DecommissionDialog";
import ServiceReportDialog from "./ServiceReportDialog";
import ReportProblemDialog from "./ReportProblemDialog";
import ProfileSidebarCard from "./ProfileSidebarCard";
import MidasReadinessProfileCard from "./readiness/MidasReadinessProfileCard";
import useTranslation from "../../../shared/hooks/useTranslation";
import { getMachineDisplayName } from "../../../shared/utils/machineUtils";
import { dt } from "../utils/dt";
import PermissionGuard from "../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../core/models/PermissionModels";
import { getUiLocale } from "../../../shared/utils/relativeTime";

// =====================================================================
//  TAB KEYS (Dashboard removed)
// =====================================================================

const BASE_TAB_KEYS = [
  "serviceReports",
  "partsAndConsumables",
  "manualsAndSoftware",
] as const;

const CATALOG_TAB_KEYS = [
  "serviceReports",
  "partsAndConsumables",
  "manualsAndSoftware",
  "specifications",
] as const;

type TabKey = typeof BASE_TAB_KEYS[number] | "specifications";

// =====================================================================
//  MACHINE PROFILE PAGE
// =====================================================================

const Machine = () => {
  useRedesignSetup();

  const { machine, loading, success, error } =
    useAppSelector<CustomerMachineState>((state: any) => state.customerMachinery);

  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation("myMachinery");

  // 3-tier responsive
  const isPhone = useMediaQuery("(max-width:768px)");
  const isTablet = useMediaQuery("(min-width:769px) and (max-width:1024px)");

  const hasCatalogModel = !!machine?.catalogModelId;
  const isDecommissioned = machine?.isDecommissioned === true;

  const defaultTab: TabKey = "serviceReports";
  const [tab, setTab] = useState<TabKey>(defaultTab);
  const [restoring, setRestoring] = useState(false);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [decommissionDialogOpen, setDecommissionDialogOpen] = useState(false);
  const [serviceReportDialogOpen, setServiceReportDialogOpen] = useState(false);
  const [reportProblemDialogOpen, setReportProblemDialogOpen] = useState(false);

  const handleChangeTab = (_: React.SyntheticEvent, newValue: TabKey) => {
    setTab(newValue);
  };

  // Load machine data
  useEffect(() => {
    if (!id) {
      navigate("/machinery");
      return;
    }
    dispatch(getMachine(id));
  }, [id]);

  // Set default tab when machine first loads
  useEffect(() => {
    if (machine) {
      setTab("serviceReports");
    }
  }, [machine?.id]);

  const handleRestore = async () => {
    if (!machine || restoring) return;
    setRestoring(true);
    try {
      await dispatch(restoreMachine(machine.id)).unwrap();
      dispatch(setSuccess(translate("restored")));
      await dispatch(getMachine(machine.id));
    } catch (err) {
      dispatch(setError(err));
    } finally {
      setRestoring(false);
    }
  };

  const handleEditDialogClose = () => setEditDialogOpen(false);
  const handleEditDialogSaved = () => {
    setEditDialogOpen(false);
    if (machine) dispatch(getMachine(machine.id));
  };

  const handleDecommissioned = () => {
    setDecommissionDialogOpen(false);
    if (machine) dispatch(getMachine(machine.id));
  };

  // =====================================================================
  //  LOADING SKELETON
  // =====================================================================

  if (loading && !machine) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          backgroundColor: dt.pageBg,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${dt.profile.dotGrid.color} 1px, transparent 0)`,
          backgroundSize: dt.profile.dotGrid.size,
          fontFamily: dt.font,
          px: { xs: "16px", md: "24px" },
          py: "24px",
        }}
      >
        {/* Breadcrumb skeleton */}
        <Skeleton
          variant="text"
          width={180}
          height={24}
          sx={{ mb: "20px", borderRadius: dt.radiusSm }}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isPhone
              ? "1fr"
              : isTablet
              ? `${dt.profile.sidebarWidth.md} 1fr`
              : `${dt.profile.sidebarWidth.lg} 1fr`,
            gap: "24px",
          }}
        >
          {/* Sidebar skeleton */}
          <Box
            sx={{
              backgroundColor: dt.cardBg,
              borderRadius: dt.machinery.card.radius,
              border: `1px solid ${dt.cardBorder}`,
              padding: "20px",
              boxShadow: dt.cardShadow,
            }}
          >
            <Skeleton
              variant="rectangular"
              width={200}
              height={200}
              sx={{ mx: "auto", mb: "16px", borderRadius: dt.radiusSm }}
            />
            <Skeleton variant="text" width="80%" height={28} sx={{ mx: "auto" }} />
            <Skeleton variant="text" width="60%" height={20} sx={{ mx: "auto", mt: "4px" }} />
            <Box sx={{ mt: "16px", pt: "16px", borderTop: `1px dashed ${dt.border.light}` }}>
              <Skeleton variant="text" width="100%" height={18} sx={{ mb: "6px" }} />
              <Skeleton variant="text" width="100%" height={18} sx={{ mb: "6px" }} />
              <Skeleton variant="text" width="100%" height={18} />
            </Box>
          </Box>

          {/* Tabs skeleton */}
          <Box>
            <Box sx={{ display: "flex", gap: "16px", mb: "24px" }}>
              {[120, 140, 170, 130].map((w, i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  width={w}
                  height={36}
                  sx={{ borderRadius: dt.radiusSm }}
                />
              ))}
            </Box>
            <Skeleton
              variant="rectangular"
              height={300}
              sx={{ borderRadius: dt.radiusSm }}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  // =====================================================================
  //  ERROR STATE (machine not found)
  // =====================================================================

  if (!loading && !machine) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          backgroundColor: dt.pageBg,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${dt.profile.dotGrid.color} 1px, transparent 0)`,
          backgroundSize: dt.profile.dotGrid.size,
          fontFamily: dt.font,
          minHeight: "400px",
          gap: "16px",
          animation: "fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) both",
        }}
      >
        <BuildCircleOutlined
          sx={{ fontSize: "64px", color: dt.textMuted }}
        />
        <Box
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            fontFamily: dt.font,
            color: dt.textPrimary,
          }}
        >
          {translate("machineNotFound")}
        </Box>
        <Box
          onClick={() => navigate("/machinery")}
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            fontFamily: dt.font,
            color: dt.blue,
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {translate("backToEquipment")}
        </Box>
      </Box>
    );
  }

  if (!machine) return null;

  const displayName = getMachineDisplayName(machine);

  // =====================================================================
  //  MAIN RENDER
  // =====================================================================

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        backgroundColor: dt.pageBg,
        backgroundImage: `radial-gradient(circle at 1px 1px, ${dt.profile.dotGrid.color} 1px, transparent 0)`,
        backgroundSize: dt.profile.dotGrid.size,
        fontFamily: dt.font,
        overflow: "hidden",
        px: { xs: "16px", md: "24px" },
        py: "20px",
        "& *:not(.MuiSvgIcon-root)": { fontFamily: `${dt.font}` },
      }}
    >
      {/* Notifications */}
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />
      <ActionToast
        open={!!error}
        variant="error"
        message={NotificationMessage.error(error).message || ""}
        onClose={() => dispatch(setError(undefined))}
        duration={6000}
      />

      {/* Breadcrumb */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          mb: "20px",
          animation: "headerReveal 0.5s cubic-bezier(0.4, 0, 0.2, 1) both",
        }}
      >
        <Box
          onClick={() => navigate("/machinery")}
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            fontFamily: dt.font,
            color: dt.profile.breadcrumb.linkColor,
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {translate("breadcrumbEquipment")}
        </Box>
        <ChevronRight
          sx={{
            fontSize: "16px",
            color: dt.profile.breadcrumb.separator,
          }}
        />
        <Box
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            fontFamily: dt.font,
            color: dt.profile.breadcrumb.currentColor,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: { xs: "200px", md: "400px" },
          }}
        >
          {displayName}
        </Box>
      </Box>

      {/* Decommissioned banner (restyled with dt tokens) */}
      {isDecommissioned && (
        <Box
          sx={{
            mb: "20px",
            p: "16px 20px",
            background: dt.decommissioned.warning.bg,
            borderLeft: `3px solid ${dt.decommissioned.warning.border}`,
            borderRadius: dt.radiusSm,
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            fontFamily: dt.font,
            animation: "fadeUp 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both",
          }}
        >
          <WarningAmberOutlined
            sx={{
              color: dt.decommissioned.warning.border,
              fontSize: "22px",
              mt: "1px",
              flexShrink: 0,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                fontFamily: dt.font,
                color: dt.decommissioned.warning.text,
                lineHeight: 1.4,
              }}
            >
              {translate("decommissionedBannerTitle")}
            </Box>
            {machine.decommissionedDate && (
              <Box
                sx={{
                  fontSize: "13px",
                  fontFamily: dt.font,
                  color: dt.decommissioned.warning.text,
                  mt: "2px",
                  opacity: 0.85,
                }}
              >
                {translate("decommissionedOn", {
                  date: new Date(machine.decommissionedDate).toLocaleDateString(getUiLocale()),
                })}
              </Box>
            )}
            {machine.decommissionReason && (
              <Box
                sx={{
                  fontSize: "13px",
                  fontStyle: "italic",
                  fontFamily: dt.font,
                  color: dt.decommissioned.warning.text,
                  mt: "4px",
                  opacity: 0.75,
                }}
              >
                &ldquo;{machine.decommissionReason}&rdquo;
              </Box>
            )}
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RestoreOutlined />}
            disabled={restoring}
            onClick={handleRestore}
            sx={{
              flexShrink: 0,
              color: "#22C55E",
              borderColor: "#22C55E",
              textTransform: "none",
              fontWeight: 600,
              fontFamily: dt.font,
              fontSize: "13px",
              borderRadius: dt.radiusSm,
              "&:hover": {
                borderColor: "#16A34A",
                backgroundColor: "rgba(34,197,94,0.06)",
              },
            }}
          >
            {restoring ? translate("saving") : translate("restore")}
          </Button>
        </Box>
      )}

      {/* =====================================================================
          MAIN GRID: sidebar + tabs
          ===================================================================== */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isPhone
            ? "1fr"
            : isTablet
            ? `${dt.profile.sidebarWidth.md} 1fr`
            : `${dt.profile.sidebarWidth.lg} 1fr`,
          gap: "24px",
          flex: 1,
          minHeight: 0,
          overflow: isPhone ? "visible" : "hidden",
          opacity: isDecommissioned ? 0.75 : 1,
        }}
      >
        {/* =====================================================================
            SIDEBAR
            ===================================================================== */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isPhone ? "row" : "column",
            gap: "16px",
            overflowY: isPhone ? "visible" : "auto",
            animation: "fadeLeft 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both",
            /* Styled scrollbar */
            "&::-webkit-scrollbar": { width: "5px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(0,0,0,0.12)",
              borderRadius: "3px",
              "&:hover": { background: "rgba(0,0,0,0.2)" },
            },
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(0,0,0,0.12) transparent",
          }}
        >
          {/* Phone: horizontal compact layout */}
          {isPhone ? (
            <PhoneSidebarCompact machine={machine} />
          ) : (
            <ProfileSidebarCard machine={machine} />
          )}

          {/* DR-3: MIDAS Data Readiness card — between the identity card and the
              action buttons. Renders nothing for machines without a catalog link.
              Desktop/tablet only (phone uses the compact sidebar). */}
          {!isPhone && <MidasReadinessProfileCard machine={machine} />}

          {/* Quick-action buttons */}
          <Box
            sx={{
              display: "flex",
              flexDirection: isPhone ? "row" : "column",
              gap: "8px",
              ...(isPhone ? { flexWrap: "wrap", flex: 1 } : {}),
            }}
          >
            {/* Edit -- hide for decommissioned and read-only users */}
            {!isDecommissioned && (
              <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                <QuickActionButton
                  icon={<EditOutlined sx={{ fontSize: "18px" }} />}
                  label={translate("editMachine")}
                  tooltip={translate("editMachineTooltip")}
                  onClick={() => setEditDialogOpen(true)}
                  isPhone={isPhone}
                />
              </PermissionGuard>
            )}

            {/* QR Code -- Write+ (SEC-REQ-B7-34 option (i), owner-adopted
                2026-08-11). Since D7-FE1 this dialog encodes the machine's
                digital-nameplate id-link, fetched from the Machinery:Write
                nameplate GET (SEC-REQ-B7-12); a Read-level link endpoint was
                REJECTED by Security. Ungating this would give Workers a 403 and
                an empty dialog. Do not remove the guard. */}
            <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
              <QuickActionButton
                icon={<QrCode2Outlined sx={{ fontSize: "18px" }} />}
                label={translate("generateQrCode")}
                tooltip={translate("qrCodeTooltip")}
                onClick={() => setQrDialogOpen(true)}
                isPhone={isPhone}
              />
            </PermissionGuard>

            {/* Report Problem -- terracotta, visible to all roles (Read-level), hide for decommissioned */}
            {!isDecommissioned && (
              <QuickActionButton
                icon={<ReportProblemOutlined sx={{ fontSize: "18px" }} />}
                label={translate("reportProblem.button")}
                tooltip={translate("reportProblem.tooltip")}
                accentColor="#D4572B"
                onClick={() => setReportProblemDialogOpen(true)}
                isPhone={isPhone}
              />
            )}

            {/* Create Service Report -- amber, hide for decommissioned and read-only users */}
            {!isDecommissioned && (
              <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                <QuickActionButton
                  icon={<NoteAddOutlined sx={{ fontSize: "18px" }} />}
                  label={translate("createServiceReport")}
                  tooltip={translate("createServiceReportTooltip")}
                  accentColor="#F59E0B"
                  onClick={() => setServiceReportDialogOpen(true)}
                  isPhone={isPhone}
                />
              </PermissionGuard>
            )}

            {/* Create Service Request -- blue, hide for decommissioned and read-only users */}
            {!isDecommissioned && (
              <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                <QuickActionButton
                  icon={<HandymanOutlined sx={{ fontSize: "18px" }} />}
                  label={translate("createServiceRequest")}
                  tooltip={translate("createServiceRequestTooltip")}
                  accentColor="#3B82F6"
                  onClick={() =>
                    navigate(`/service-requests/new?machine=${machine.id}&returnTo=/machinery/${machine.id}`)
                  }
                  isPhone={isPhone}
                />
              </PermissionGuard>
            )}

            {/* Decommission -- red, hide when already decommissioned and read-only users */}
            {!isDecommissioned && (
              <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                <QuickActionButton
                  icon={<PowerSettingsNewOutlined sx={{ fontSize: "18px" }} />}
                  label={translate("decommission")}
                  tooltip={translate("decommissionTooltip")}
                  accentColor="#EF4444"
                  onClick={() => setDecommissionDialogOpen(true)}
                  isPhone={isPhone}
                />
              </PermissionGuard>
            )}
          </Box>
        </Box>

        {/* =====================================================================
            TABS + CONTENT
            ===================================================================== */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            overflow: "hidden",
            animation: "fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both",
          }}
        >
          <TabContext value={tab}>
            {/* Styled TabList */}
            <Box
              sx={{
                mb: "20px",
                borderBottom: `1px solid ${dt.cardBorder}`,
              }}
            >
              {/* L6 (USER-LANG-IT-PL-1): scrollable at EVERY width. The 4 tabs need ~624px in
                  EN but ~751 / 761px in DE / PL ("Ersatzteile und Verbrauchsmaterial"); with
                  "standard" the MUI scroller clips overflow, so the last tab became unreachable
                  on narrow tablets. Scrollable renders identically when the tabs fit. */}
              <TabList
                onChange={handleChangeTab}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: "40px",
                  "& .MuiTabs-indicator": {
                    backgroundColor: dt.profile.tab.indicator,
                    height: "3px",
                    borderRadius: "3px 3px 0 0",
                  },
                  "& .MuiTab-root": {
                    fontFamily: dt.font,
                    fontSize: "13px",
                    fontWeight: 500,
                    textTransform: "none",
                    color: dt.profile.tab.inactiveColor,
                    minHeight: "40px",
                    padding: "8px 16px",
                    letterSpacing: "0.1px",
                    transition: "color 0.2s ease",
                    "&.Mui-selected": {
                      color: dt.profile.tab.activeColor,
                      fontWeight: 600,
                    },
                    "&:hover": {
                      color: dt.profile.tab.activeColor,
                    },
                  },
                }}
              >
                <Tab
                  label={translate("serviceReportsTab")}
                  value="serviceReports"
                />
                <Tab
                  label={translate("partsAndConsumablesTab")}
                  value="partsAndConsumables"
                />
                <Tab
                  label={translate("manualsAndSoftwareTab")}
                  value="manualsAndSoftware"
                />
                {hasCatalogModel && (
                  <Tab
                    label={translate("specifications")}
                    value="specifications"
                  />
                )}
              </TabList>
            </Box>

            {/* Tab content area -- scrollable */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                /* Styled scrollbar */
                "&::-webkit-scrollbar": { width: "5px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(0,0,0,0.12)",
                  borderRadius: "3px",
                  "&:hover": { background: "rgba(0,0,0,0.2)" },
                },
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(0,0,0,0.12) transparent",
              }}
            >
              <TabPanel value="serviceReports" sx={{ p: 0 }}>
                <ServiceReports machineId={machine.id} />
              </TabPanel>

              <TabPanel value="partsAndConsumables" sx={{ p: 0 }}>
                <PartsAndConsumables machineId={machine.id} />
              </TabPanel>

              <TabPanel value="manualsAndSoftware" sx={{ p: 0 }}>
                <ManualsAndSoftware machineId={machine.id} />
              </TabPanel>

              {hasCatalogModel && (
                <TabPanel value="specifications" sx={{ p: 0 }}>
                  <MachineSpecs catalogModelId={machine.catalogModelId!} />
                </TabPanel>
              )}
            </Box>
          </TabContext>
        </Box>
      </Box>

      {/* =====================================================================
          DIALOGS
          ===================================================================== */}
      <MachineEditDialog
        open={editDialogOpen}
        machine={machine}
        onClose={handleEditDialogClose}
        onSaved={handleEditDialogSaved}
      />

      {qrDialogOpen && (
        <MachineQRCodeDialog
          open={qrDialogOpen}
          machine={machine}
          onClose={() => setQrDialogOpen(false)}
        />
      )}

      <DecommissionDialog
        open={decommissionDialogOpen}
        machine={machine}
        onClose={() => setDecommissionDialogOpen(false)}
        onDecommissioned={handleDecommissioned}
      />

      <ServiceReportDialog
        open={serviceReportDialogOpen}
        onClose={() => setServiceReportDialogOpen(false)}
        machineId={machine.id}
      />

      {reportProblemDialogOpen && (
        <ReportProblemDialog
          open={reportProblemDialogOpen}
          machine={machine}
          onClose={() => setReportProblemDialogOpen(false)}
          onSuccess={(srNumber) => {
            dispatch(setSuccess(
              translate("reportProblem.successDetail", { number: srNumber })
            ));
          }}
        />
      )}
    </Box>
  );
};

// =====================================================================
//  QUICK ACTION BUTTON
// =====================================================================

interface QuickActionButtonProps {
  icon: React.ReactNode;
  label: string;
  tooltip?: string;
  accentColor?: string;
  onClick: () => void;
  isPhone: boolean;
}

const QuickActionButton = ({
  icon,
  label,
  tooltip,
  accentColor,
  onClick,
  isPhone,
}: QuickActionButtonProps) => {
  const button = (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: isPhone ? "8px 14px" : "10px 16px",
        borderRadius: dt.radiusSm,
        border: `1px solid ${dt.profile.quickAction.border}`,
        borderLeft: accentColor ? `3px solid ${accentColor}` : `1px solid ${dt.profile.quickAction.border}`,
        background: dt.cardBg,
        cursor: "pointer",
        transition: "all 0.2s ease",
        fontFamily: dt.font,
        fontSize: "13px",
        fontWeight: 500,
        color: dt.textSecondary,
        whiteSpace: "nowrap",
        "&:hover": {
          borderColor: accentColor || dt.profile.quickAction.hoverBorder,
          borderLeft: accentColor ? `3px solid ${accentColor}` : undefined,
          backgroundColor: accentColor ? `${accentColor}08` : dt.profile.quickAction.hoverBg,
          color: accentColor || dt.profile.quickAction.hoverColor,
          "& .qa-icon": {
            color: accentColor || dt.profile.quickAction.hoverColor,
          },
          "& .qa-info": {
            color: dt.textSecondary,
          },
        },
      }}
    >
      <Box
        className="qa-icon"
        sx={{
          display: "flex",
          color: accentColor || dt.textMuted,
          transition: "color 0.2s ease",
        }}
      >
        {icon}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px", fontSize: isPhone ? "12px" : "13px" }}>
        {label}
        {tooltip && (
          <InfoOutlined
            className="qa-info"
            sx={{
              fontSize: "14px",
              color: dt.textMuted,
              transition: "color 0.2s ease",
            }}
          />
        )}
      </Box>
    </Box>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow placement="right">
        {button}
      </Tooltip>
    );
  }

  return button;
};

// =====================================================================
//  PHONE SIDEBAR COMPACT (horizontal card for phone breakpoint)
// =====================================================================

const PhoneSidebarCompact = ({ machine }: { machine: any }) => {
  const { translate } = useTranslation("myMachinery");
  const displayName = getMachineDisplayName(machine);
  const isDecommissioned = machine.isDecommissioned === true;

  const imageUrl =
    machine?.image?.content ||
    machine?.catalogModel?.imageStandardUrl ||
    machine?.catalogModel?.imageUrl ||
    null;

  return (
    <Box
      sx={{
        display: "flex",
        gap: "14px",
        background: dt.cardBg,
        borderRadius: dt.machinery.card.radius,
        border: isDecommissioned
          ? dt.decommissioned.card.borderStyle
          : dt.machinery.card.border,
        padding: "14px",
        boxShadow: dt.machinery.card.shadow,
        fontFamily: dt.font,
        width: "100%",
        opacity: isDecommissioned ? dt.decommissioned.card.opacity : 1,
      }}
    >
      {/* Compact image */}
      <Box
        sx={{
          width: "80px",
          height: "80px",
          minWidth: "80px",
          borderRadius: dt.radiusSm,
          border: `1px solid ${dt.border.light}`,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#F8FAFC",
          filter: isDecommissioned
            ? dt.decommissioned.card.imageFilter
            : "none",
        }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={displayName}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <CameraIconPlaceholder />
        )}
      </Box>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            fontSize: "16px",
            fontWeight: 600,
            fontFamily: dt.font,
            color: dt.textPrimary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {displayName}
        </Box>
        {machine.serialNumber && (
          <Box
            sx={{
              fontSize: "12px",
              fontFamily: dt.fontMono,
              fontWeight: 500,
              color: dt.textSecondary,
              mt: "2px",
              letterSpacing: "0.3px",
            }}
          >
            S/N: {machine.serialNumber}
          </Box>
        )}
        {machine.catalogModelId && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "10px",
              fontWeight: 600,
              fontFamily: dt.font,
              color: dt.blue,
              backgroundColor: dt.blueSoft,
              borderRadius: "8px",
              padding: "1px 8px",
              mt: "6px",
              lineHeight: "16px",
            }}
          >
            {translate("catalogModel")}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Inline camera placeholder for phone compact layout
const CameraIconPlaceholder = () => (
  <Box
    sx={{
      width: "32px",
      height: "32px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: dt.textMuted,
      fontSize: "32px",
    }}
  >
    <BuildCircleOutlined sx={{ fontSize: "28px", color: dt.textMuted }} />
  </Box>
);

export default Machine;
